import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// Shared sortable-table primitives used across the app so every table gets the
// same primary-coloured header + asc/desc sort behaviour.

export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}

// Read a possibly-nested value by dot path, e.g. "assignedTo.name".
function getVal(row: any, key: string): any {
  if (!key) return undefined;
  return key.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), row);
}

/**
 * Client-side sort hook. Tri-state per column: asc → desc → none.
 * Sorts the rows currently in memory (for server-paginated tables this sorts
 * the visible page, which is the common datatable behaviour).
 */
export function useSort<T>(rows: T[], initial: SortState | null = null) {
  const [sort, setSort] = useState<SortState | null>(initial);

  const onSort = (key: string) =>
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears
    });

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    const factor = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getVal(a, key);
      const bv = getVal(b, key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last
      if (bv == null) return -1;
      // numbers / dates
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
      const ad = Date.parse(av), bd = Date.parse(bv);
      if (!Number.isNaN(ad) && !Number.isNaN(bd) && typeof av === 'string' && typeof bv === 'string') {
        return (ad - bd) * factor;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
    });
  }, [rows, sort]);

  return { sorted, sort, onSort };
}

/** Primary-coloured <thead> row wrapper — use around <SortTh>/<th> cells. */
export function TableHeadRow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <tr className={cn('bg-[#6366F1] text-left text-[11px] font-semibold uppercase tracking-wider text-white', className)}>
      {children}
    </tr>
  );
}

/** A sortable header cell with an asc/desc/neutral icon. */
export function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  className,
  sortable = true,
  align = 'left',
}: {
  label: React.ReactNode;
  sortKey?: string;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  className?: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
}) {
  const active = !!sortKey && sort?.key === sortKey;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  if (!sortable || !sortKey || !onSort) {
    return <th className={cn('px-4 py-3', className)}>{label}</th>;
  }

  return (
    <th className={cn('px-4 py-3', className)}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn('inline-flex w-full items-center gap-1 uppercase tracking-wider text-white/90 transition-colors hover:text-white focus:outline-none', justify)}
      >
        {label}
        {active ? (
          sort!.dir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </th>
  );
}
